<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <Loading v-if="isLoading"></Loading>
          <div v-else>
            <div class="po-detail-header mb-3">
              <i
                class="fas fa-chevron-left back-icon"
                v-b-tooltip.hover.top="'ย้อนกลับ'"
                @click="$router.go(-1)"
              ></i>
              <h4 class="mb-0">
                <b>ใบสั่งซื้อ</b>
              </h4>
              <PoStatusBadge :status="data.status" />
            </div>

            <div class="po-detail-layout">
              <!-- Left: metadata -->
              <div class="po-detail-info">
                <div class="info-row">
                  <span class="info-label">เลขที่เอกสาร:</span>
                  <span class="info-value">{{ data.poCode }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">รหัสใบขอซื้ออ้างอิง:</span>
                  <div
                    class="info-value pr-refs-list"
                    v-if="data.prRefs && data.prRefs.length > 0"
                  >
                    <span
                      v-for="pr in data.prRefs"
                      :key="pr.prId"
                      class="pr-ref-chip"
                    >
                      {{ pr.prNo }}
                      <i
                        v-b-tooltip.hover.top="'ดูข้อมูลใบขอซื้อ'"
                        class="fas fa-info-circle info-ref-icon"
                        @click="goToPrDetail(pr.prId)"
                      ></i>
                    </span>
                  </div>
                  <div
                    v-else
                    class="info-value"
                  >
                    -
                  </div>
                </div>
                <div class="info-row">
                  <span class="info-label">วันที่ทำรายการ:</span>
                  <span class="info-value">{{
                    formatDate(data.createdAt)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">รหัสผู้ขาย:</span>
                  <span class="info-value">{{ data.vendorCode }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ชื่อผู้ขาย:</span>
                  <span class="info-value">{{ data.vendorName }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ชื่อสาขา:</span>
                  <span class="info-value">{{ data.branchName || "-" }}</span>
                </div>
              </div>

              <div class="po-detail-divider"></div>

              <!-- Right: items table -->
              <div class="po-detail-table">
                <div class="items-table">
                  <!-- Header -->
                  <div class="items-table-header">
                    <div class="it-col-index"></div>
                    <div class="it-col-thumb"></div>
                    <div class="it-col-code">รหัส</div>
                    <div class="it-col-name">ชื่อสินค้า</div>
                    <div class="it-col-qty">รับแล้ว/ทั้งหมด</div>
                    <div class="it-col-uom">หน่วยสินค้า</div>
                  </div>
                  <!-- Rows -->
                  <div
                    v-for="(item, index) in poItemsWithImage"
                    :key="index"
                    class="items-table-row"
                  >
                    <div class="it-col-index">{{ index + 1 }}.</div>
                    <div class="it-col-thumb">
                      <ProductImage :src="item.imageUrl" />
                    </div>
                    <div class="it-col-code">{{ item.productCode || "-" }}</div>
                    <div class="it-col-name">{{ item.productName || "-" }}</div>
                    <div class="it-col-qty">
                      {{ item.qtyReceived }}/{{ item.qtyTotal }}
                    </div>
                    <div class="it-col-uom">{{ item.uomName || "-" }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Related Goods Receives -->
            <div class="mt-4">
              <h5><b>รายการใบรับสินค้าที่เกี่ยวข้อง</b></h5>
              <div class="gr-table">
                <!-- Header -->
                <div class="gr-table-header">
                  <div class="gr-col-index"></div>
                  <div
                    class="gr-col-code gr-th"
                    @click="toggleSort('grCode')"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.7064 19.6649L6.22674 14.266C6.15116 14.1915 6.09448 14.1108 6.05669 14.0239C6.0189 13.9371 6 13.844 6 13.7447C6 13.5461 6.06928 13.3723 6.20785 13.2234C6.34641 13.0745 6.52907 13 6.75581 13H18.2442C18.4709 13 18.6536 13.0745 18.7922 13.2234C18.9307 13.3723 19 13.5461 19 13.7447C19 13.7943 18.9244 13.9681 18.7733 14.266L13.2936 19.6649C13.1676 19.789 13.0417 19.8759 12.9157 19.9255C12.7897 19.9752 12.6512 20 12.5 20C12.3488 20 12.2103 19.9752 12.0843 19.9255C11.9583 19.8759 11.8324 19.789 11.7064 19.6649Z"
                        :fill="
                          grSortBy === 'grCode' && grSortDesc
                            ? '#212529'
                            : '#A3A4A6'
                        "
                      ></path>
                      <path
                        d="M13.2936 4.33511L18.7733 9.73404C18.8488 9.80851 18.9055 9.88918 18.9433 9.97606C18.9811 10.0629 19 10.156 19 10.2553C19 10.4539 18.9307 10.6277 18.7922 10.7766C18.6536 10.9255 18.4709 11 18.2442 11L6.75581 11C6.52907 11 6.34641 10.9255 6.20785 10.7766C6.06928 10.6277 6 10.4539 6 10.2553C6 10.2057 6.07558 10.0319 6.22674 9.73404L11.7064 4.33511C11.8324 4.21099 11.9583 4.12411 12.0843 4.07447C12.2103 4.02482 12.3488 4 12.5 4C12.6512 4 12.7897 4.02482 12.9157 4.07447C13.0417 4.12411 13.1676 4.21099 13.2936 4.33511Z"
                        :fill="
                          grSortBy === 'grCode' && !grSortDesc
                            ? '#212529'
                            : '#A3A4A6'
                        "
                      ></path>
                    </svg>
                    <span>รหัสใบรับสินค้า</span>
                  </div>
                  <div
                    class="gr-col-date gr-th"
                    @click="toggleSort('createdAt')"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.7064 19.6649L6.22674 14.266C6.15116 14.1915 6.09448 14.1108 6.05669 14.0239C6.0189 13.9371 6 13.844 6 13.7447C6 13.5461 6.06928 13.3723 6.20785 13.2234C6.34641 13.0745 6.52907 13 6.75581 13H18.2442C18.4709 13 18.6536 13.0745 18.7922 13.2234C18.9307 13.3723 19 13.5461 19 13.7447C19 13.7943 18.9244 13.9681 18.7733 14.266L13.2936 19.6649C13.1676 19.789 13.0417 19.8759 12.9157 19.9255C12.7897 19.9752 12.6512 20 12.5 20C12.3488 20 12.2103 19.9752 12.0843 19.9255C11.9583 19.8759 11.8324 19.789 11.7064 19.6649Z"
                        :fill="
                          grSortBy === 'createdAt' && grSortDesc
                            ? '#212529'
                            : '#A3A4A6'
                        "
                      ></path>
                      <path
                        d="M13.2936 4.33511L18.7733 9.73404C18.8488 9.80851 18.9055 9.88918 18.9433 9.97606C18.9811 10.0629 19 10.156 19 10.2553C19 10.4539 18.9307 10.6277 18.7922 10.7766C18.6536 10.9255 18.4709 11 18.2442 11L6.75581 11C6.52907 11 6.34641 10.9255 6.20785 10.7766C6.06928 10.6277 6 10.4539 6 10.2553C6 10.2057 6.07558 10.0319 6.22674 9.73404L11.7064 4.33511C11.8324 4.21099 11.9583 4.12411 12.0843 4.07447C12.2103 4.02482 12.3488 4 12.5 4C12.6512 4 12.7897 4.02482 12.9157 4.07447C13.0417 4.12411 13.1676 4.21099 13.2936 4.33511Z"
                        :fill="
                          grSortBy === 'createdAt' && !grSortDesc
                            ? '#212529'
                            : '#A3A4A6'
                        "
                      ></path>
                    </svg>
                    <span>วันที่สร้าง</span>
                  </div>
                  <div class="gr-col-action"></div>
                </div>
                <!-- Rows -->
                <div
                  v-for="(item, index) in sortedGoodsReceives"
                  :key="item.grCode"
                  class="gr-table-row"
                >
                  <div class="gr-col-index">{{ index + 1 }}.</div>
                  <div class="gr-col-code">{{ item.grCode }}</div>
                  <div class="gr-col-date">
                    {{ formatDate(item.createdAt) }}
                  </div>
                  <div class="gr-col-action">
                    <ActionButtonGroup
                      :item="item"
                      :show-edit="false"
                      :show-delete="false"
                      infoTooltip="ดูข้อมูลใบรับสินค้า"
                      @info="openGoodsReceiveDetail(item)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <GoodsReceiveDetail ref="goodsReceiveDetailModal"></GoodsReceiveDetail>
    </div>
  </div>
</template>

<script>
import { THAI_MONTHS } from "@/constants";
import { mapActions, mapGetters } from "vuex";
import PoStatusBadge from "@/components/badge/PoStatusBadge.vue";
import GoodsReceiveDetail from "@/components/modal/ProductAndStock/GoodsReceiveDetail.vue";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import ActionButtonGroup from "@/components/table/stock/button/ActionButtons.vue";
import Loading from "@/components/Loading";

export default {
  name: "PurchaseOrderDetail",
  components: {
    PoStatusBadge,
    GoodsReceiveDetail,
    ActionButtonGroup,
    Loading,
    ProductImage,
  },
  data() {
    return {
      isLoading: false,
      data: {
        poCode: "",
        prId: null,
        prRefCode: "",
        prRefs: [],
        createdAt: "",
        vendorCode: "",
        vendorName: "",
        branchName: "",
        status: "",
        items: [],
        goodsReceives: [],
      },
      grSortBy: null,
      grSortDesc: false,
    };
  },
  computed: {
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
    }),
    poItemsWithImage() {
      return (this.data.items || []).map(item => ({
        ...item,
        imageUrl:
          this.getProductList.find(
            pl =>
              pl.productId === item.productId ||
              pl.productCode === item.productCode
          )?.imageUrl || null,
      }));
    },
    sortedGoodsReceives() {
      const items = [...(this.data.goodsReceives || [])];
      if (!this.grSortBy) return items;
      return items.sort((a, b) => {
        const valA = a[this.grSortBy] || "";
        const valB = b[this.grSortBy] || "";
        const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
        return this.grSortDesc ? -cmp : cmp;
      });
    },
  },
  created() {
    this.fetchDetail();
  },
  methods: {
    ...mapActions({
      fetchPurchaseOrderById: "modulePurchaseOrder/fetchPurchaseOrderById",
    }),
    fetchDetail() {
      this.isLoading = true;
      this.fetchPurchaseOrderById({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        id: this.$route.params.id,
      })
        .then(detail => {
          if (detail) {
            this.data = detail;
          }
        })
        .catch(err => {
          console.error("fetchPurchaseOrderById failed", err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    toggleSort(field) {
      if (this.grSortBy === field) {
        this.grSortDesc = !this.grSortDesc;
      } else {
        this.grSortBy = field;
        this.grSortDesc = false;
      }
    },
    goToPrDetail(prId) {
      if (!prId) return;
      this.$router.push({
        name: "PurchaseRequestDetail",
        params: {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          prId,
        },
      });
    },
    openGoodsReceiveDetail(grItem) {
      this.$refs.goodsReceiveDetailModal.show(grItem);
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = THAI_MONTHS[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${day} ${month} ${year}`;
    },
  },
};
</script>

<style scoped>
.back-icon {
  font-size: 1.2rem;
  cursor: pointer;
  color: #333;
  margin-left: -4px;
}

.back-icon:hover {
  color: #1976d2;
}

.po-detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.po-detail-layout {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 24px 24px;
  border: 1px solid #dee2e6;
}

.po-detail-info {
  flex: 0 0 428px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.po-detail-divider {
  align-self: stretch;
  width: 1px;
  background-color: #dee2e6;
  flex-shrink: 0;
}

.po-detail-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.info-label {
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
  font-size: 1rem;
}

.info-value {
  color: #212529;
  font-size: 1rem;
}

.info-ref-icon {
  color: #a3a4a6;
  cursor: pointer;
  font-size: 1.1rem;
  transition: color 0.15s;
}

.info-ref-icon:hover {
  color: #1976d2;
}

/* Multi-PR chips */
.pr-refs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.product-thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

/* Items table */
.items-table {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.items-table-header {
  display: flex;
  align-items: center;
  background-color: #e9ecef;
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  min-width: 600px;
}

.items-table-row {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-bottom: 1px solid #dee2e6;
  min-width: 600px;
}

.items-table-row:last-child {
  border-bottom: none;
}

.items-table-header > div,
.items-table-row > div {
  padding: 8px;
  font-size: 1rem;
  color: #212529;
}

.items-table-header > div {
  font-weight: 600;
}

.it-col-index {
  flex: 0 0 40px;
}
.it-col-thumb {
  flex: 0 0 56px;
}
.it-col-code {
  flex: 0 0 96px;
}
.it-col-name {
  flex: 1;
}
.it-col-qty {
  flex: 0 0 240px;
  text-align: right;
  justify-content: flex-end;
  display: flex;
}
.it-col-uom {
  flex: 0 0 200px;
}

.gr-table {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.gr-table-header {
  display: flex;
  align-items: center;
  background-color: #e9ecef;
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  min-width: 400px;
}

.gr-table-row {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-top: 1px solid #dee2e6;
  min-width: 400px;
}

.gr-table-row:first-of-type {
  border-top: none;
}

.gr-col-index {
  flex: 0 0 80px;
  padding: 8px 16px;
  color: #212529;
}

.gr-col-code {
  flex: 1;
  padding: 16px 24px;
  color: #212529;
}

.gr-col-date {
  flex: 0 0 200px;
  padding: 16px 24px;
  color: #212529;
}

.gr-col-action {
  flex: 0 0 72px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gr-th {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;
}

.gr-th svg {
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .po-detail-layout {
    flex-direction: column;
  }

  .po-detail-info {
    flex: none;
    width: 100%;
  }

  .po-detail-divider {
    width: 100%;
    height: 1px;
  }
}
</style>
